import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Snackbar, Alert, AlertTitle, Box } from "@mui/material";

/**
 * MUI Snackbar host shared across the Remediation Hub feature (no notistack).
 * Matches the inline Snackbar+Alert pattern used elsewhere in the app
 * (see src/pages/CSPM/index.js, src/pages/OfflineCur/index.js).
 *
 * Usage:
 *   <RemediationSnackbarProvider>
 *     ...children...
 *   </RemediationSnackbarProvider>
 *
 *   const { showSnackbar } = useRemediationSnackbar();
 *   showSnackbar({ message: "Approved 3 requests.", severity: "success" });
 */

const RemediationSnackbarContext = createContext(null);

export const RemediationSnackbarProvider = ({ children }) => {
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
    autoHideDuration: 4000,
    title: null,
    details: null,
  });

  const showSnackbar = useCallback(
    ({ message, severity = "info", autoHideDuration, title, details }) => {
      setSnack({
        open: true,
        message: message || "",
        severity,
        // No auto-hide on errors by default — user dismisses.
        autoHideDuration:
          autoHideDuration !== undefined
            ? autoHideDuration
            : severity === "error"
              ? null
              : severity === "warning"
                ? 6000
                : 4000,
        title: title || null,
        details: details || null,
      });
    },
    []
  );

  const handleClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setSnack((s) => ({ ...s, open: false }));
  };

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <RemediationSnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        open={snack.open}
        autoHideDuration={snack.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          severity={snack.severity}
          onClose={handleClose}
          variant="filled"
          sx={{ minWidth: 320, maxWidth: 540, alignItems: "flex-start" }}
        >
          {snack.title && <AlertTitle sx={{ mb: 0.5 }}>{snack.title}</AlertTitle>}
          <Box sx={{ fontSize: 14 }}>{snack.message}</Box>
          {snack.details && snack.details.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5, fontSize: 13 }}>
              {snack.details.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </Box>
          )}
        </Alert>
      </Snackbar>
    </RemediationSnackbarContext.Provider>
  );
};

export const useRemediationSnackbar = () => {
  const ctx = useContext(RemediationSnackbarContext);
  if (!ctx) {
    // Render-time safety fallback — surface a console warning instead of crashing
    // if a caller forgot the provider (e.g. when the button is used outside the
    // Remediation Hub on the CSPM page).
    return {
      showSnackbar: (opts) => {
        // eslint-disable-next-line no-console
        console.warn(
          "useRemediationSnackbar called outside <RemediationSnackbarProvider>; falling back to console.",
          opts
        );
      },
    };
  }
  return ctx;
};

export default RemediationSnackbarProvider;
